import {
  Component,
  Inject,
  input,
  output,
  viewChild,
  computed,
  TemplateRef
} from '@angular/core';
import { FormControl } from '@angular/forms';

import { toSignal } from '@angular/core/rxjs-interop';

import { ProfileEditFormComponent } from '@sondermind/features/account-edit';
import { IrisButtonContainerAlignments } from '@sondermind/iris/button';

import { FEATURE_FLAG_SERVICE, FeatureFlagKey, IFeatureFlagService } from '@sondermind/data-access/feature-flags';

import { SetupAttestationAgreeComponent } from '../setup-attestation-agree/setup-attestation-agree.component';

@Component({
  selector: 'client-setup-basic-info',
  templateUrl: './setup-basic-info.component.html',
  styleUrls: ['./setup-basic-info.component.scss'],
  standalone: false
})
export class SetupBasicInfoComponent {
  readonly useInSessionRequest = input<boolean>(false);
  readonly isMobile = input<boolean>(false);
  readonly isProposeATime = input<boolean>(false);
  readonly fromDirectBooking = input<boolean>(false);

  readonly basicInfoSubmitted = output<void>();

  readonly profileEditForm = viewChild.required<ProfileEditFormComponent>('profileEditForm');
  // only present if new ToS modal is not enabled
  readonly attestationComponent = viewChild<SetupAttestationAgreeComponent>('attestationAgree');

  // Template for the action button that parent component can render
  readonly actionButtonTemplate = viewChild.required<TemplateRef<unknown>>('actionButton');

  readonly irisButtonContainerAlignments = IrisButtonContainerAlignments;

  readonly tosModalEnabled = toSignal(
    this.featureFlagService.isEnabled$(FeatureFlagKey.NEW_TOS_MODAL),
    { initialValue: false }
  );

  constructor(
    @Inject(FEATURE_FLAG_SERVICE)
    private featureFlagService: IFeatureFlagService
  ) {}

  /**
   * Submit profile edit form, success will trigger attestation signing.
   */
  submitBasicInfo(): void {
    this.profileEditForm().submitForm();
  }

  /**
   * After basic info successfully submits,
   * if ToS modal is enabled, just emit the event to let parent know basic info step is done,
   * otherwise sign attestations.
   */
  profileInfoSubmitted(): void {
    if (this.tosModalEnabled()) {
      this.basicInfoSubmitted.emit();
    } else {
      this.attestationComponent()?.signAttestations();
    }
  }

  /**
   * After attestations successfully submit, let parent know basic info step is done.
   */
  attestationsSubmitted(): void {
    this.basicInfoSubmitted.emit();
  }

  get isSubmitDisabled(): boolean {
    const attestationGoodToGo = computed(() => {
      // if ToS modal is enabled, we don't need to check attestation form value
      if (this.tosModalEnabled()) {
        return true;
      }

      const attestationFormValue = this.attestationComponent()?.setupForm?.value as {
        agreedHipaa: FormControl<boolean | null>;
        agreedTos: FormControl<boolean | null>;
      };

      // not submitting and both attestations are checked
      return !this.attestationComponent()?.submitting &&
        !!attestationFormValue?.agreedHipaa &&
        !!attestationFormValue?.agreedTos;
    });

    return !this.profileEditForm()?.profileForm?.valid || this.profileEditForm()?.submitting || !attestationGoodToGo();
  }
}
